import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Warning } from "@livechat/design-system-icons";
import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { IAgent, IWithAgentsState, selectAgentsAlphabetically, selectLoggedInAgent } from "store/entities/agents";
import { IRuleActionAddFollowers, IRuleActionRemoveFollowers } from "store/entities/rules/interfaces";
import { IWithSessionState } from "store/features/session";

import { changeActionValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleActionAddFollowers | IRuleActionRemoveFollowers>;
}

export const Followers: FC<IProps> = ({ input }) => {
  const loggedInAgent = useSelector(selectLoggedInAgent);
  const availableFollowers = useSelector((state: IWithAgentsState & IWithSessionState) =>
    selectAgentsAlphabetically(state, {
      activeOnly: true,
      loggedInAgentOnTop: true,
    })
  );
  const options = availableFollowers.map(agent => createAgentOption(agent, loggedInAgent));
  const selected = input.value.followers.map(id => options.find(o => o.key === id) ?? createDeletedAgent(id));

  const onSelect = (selected: IPickerListItem[] | null): void => {
    changeActionValues(input, { followers: selected?.map(s => s.key) ?? [] });
  };

  return (
    <Picker
      options={options}
      onSelect={onSelect}
      placeholder="Select one follower or more…"
      type="multi"
      selected={selected}
      hideClearButton
      data-testid="followers-picker"
    />
  );
};

const createAgentOption = (agent: IAgent, loggedInAgent: IAgent): IPickerListItem => ({
  key: agent.id,
  name: agent.name,
  customElement: {
    selectedItemBody: <AgentPickerSelectedListItem agentName={agent.name} avatarUrl={agent.avatar} />,
    listItemBody: (
      <AgentPickerListItem
        agentName={agent.name}
        avatarUrl={agent.avatar}
        isLoggedInAgent={agent.id === loggedInAgent.id}
      />
    ),
  },
});

const createDeletedAgent = (id: string): IPickerListItem => ({
  key: id,
  name: "Follower removed",
  icon: Warning,
  selectedTagOptions: {
    kind: "error",
  },
});
